import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import type { ReactNode, Ref } from "react";
import { Link } from "react-router-dom";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { MetaList } from "@/profitability/meta-list";
import { isMissing, MISSING_LABEL } from "@/profitability/missing";
import type { TemplateSetting } from "@/profitability/settings/types";
import type { ReportColumn } from "@/profitability/types";
import { fileTitle, partPlace, partRemark, shareWords } from "./figure-words";
import { isFilePart } from "./sourced";
import type { FigureHow, FigurePart } from "./types";
import { useFigureFormat } from "./use-figure-format";

/**
 * One part of a figure, under it: a cell of a file ("Row 5, Gross", its
 * file and what the script noted), another figure of the record (choosing
 * it moves there), a setting (a link to the template's Settings) or a bare
 * value. A part shown but not counted (left out, a candidate not picked)
 * reads quieter and says so. Choosing a part moves the file to its cell.
 */
export const SourcePartRow = ({
  ref,
  how,
  part,
  column,
  operand,
  setting,
  settingsHref,
  active,
  focusable,
  onSelect,
  onFollow,
  onLeave,
}: {
  ref?: Ref<HTMLLIElement>;
  /** How the figure it is part of was made. */
  how: FigureHow;
  part: FigurePart;
  /** The figure's column, which a file's cell is written as. */
  column: ReportColumn | null;
  /** The column of the figure this part is, when it is one. */
  operand: ReportColumn | null;
  /** The setting this part is, when it is one and the run declared it. */
  setting: TemplateSetting | undefined;
  /** The template's Settings page, keeping the period. */
  settingsHref: string;
  active: boolean;
  focusable: boolean;
  onSelect: () => void;
  /** Moves to the figure this part is; absent when it cannot be shown. */
  onFollow?: () => void;
  /** Called as the Settings link leaves the dialog. */
  onLeave: () => void;
}) => {
  const format = useFigureFormat();
  const place = partPlace(how, part);
  // A figure it is worked out from is missing, a setting not set; only a
  // file's cell is blank.
  const value = isMissing(part.value)
    ? part.figure
      ? MISSING_LABEL
      : part.setting
        ? "Not set"
        : "Blank"
    : part.setting
      ? format.setting(part.value, setting)
      : format.value(part.value, part.figure ? operand : column);
  const meta: ReactNode[] = [
    !part.used && "Not counted",
    isFilePart(part) && fileTitle(part),
    partRemark(how, part),
    shareWords(part.share),
    part.setting && "Setting",
  ];
  const label = part.setting ? (
    <Link
      to={settingsHref}
      tabIndex={-1}
      onClick={(event) => {
        event.stopPropagation();
        onLeave();
      }}
      className="inline-flex min-w-0 items-center gap-1.5 text-link hover:underline"
    >
      <GearSixIcon aria-hidden className="size-3.5 flex-none" />
      <span className="truncate">{place}</span>
    </Link>
  ) : part.figure && onFollow ? (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-link">
      <span className="truncate">{place}</span>
      <ArrowRightIcon aria-hidden className="size-3.5 flex-none" />
    </span>
  ) : (
    <span className="truncate">{place}</span>
  );
  return (
    <li
      ref={ref}
      role="treeitem"
      aria-level={2}
      aria-selected={active}
      tabIndex={focusable ? 0 : -1}
      onClick={(event) => {
        event.stopPropagation();
        if (part.figure && onFollow) onFollow();
        else onSelect();
      }}
      className={cn(
        "grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 rounded-md px-2.5 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset",
        {
          "bg-surface-raised ring-1 ring-line-strong ring-inset": active,
          "hover:bg-neutral-subtle-hovered": !active,
          "text-subtle": !part.used,
        },
      )}
    >
      <Text render={<span />} className="flex min-w-0 items-center">
        {label}
      </Text>
      <Text
        render={<span />}
        tabular
        className={cn("text-right", {
          "text-subtle": !part.used || isMissing(part.value),
        })}
      >
        {value}
      </Text>
      {meta.some(Boolean) && (
        <Text
          variant="caption"
          tone="muted"
          render={<span />}
          className="col-span-2 min-w-0"
        >
          <MetaList>{meta}</MetaList>
        </Text>
      )}
    </li>
  );
};
